import React from 'react';
import { getBranding, type BrandingConfig } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import {
  CheckCircle2,
  Download,
} from 'lucide-react';

import {
  type AppIdentity,
  type BillingAccessState,
  createCheckoutSession,
  createPortalSession,
  fetchBillingStatus,
  getStoredAppIdentity,
} from '../../lib/billing.ts';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';
import { getCurrentSession } from '../../lib/auth.ts';

type BillingPanelProps = {
  identity?: AppIdentity | null;
  initialState?: BillingAccessState | null;
  onStatusChange?: (state: BillingAccessState) => void;
  embedded?: boolean;
  checkoutButtonLabel?: string;
  isAdminAccount?: boolean;
  partnerMonthlyPrice?: number | null;
};

const MONTHLY_INVOICE_AMOUNT_LABEL = '$39.00';
const YEARLY_INVOICE_AMOUNT_LABEL = '$390.00';

type BillingPricingCopy = BrandingConfig['copy']['billing']['pricing'];

function normalizeStatus(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, ' ');
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function formatLongDate(value: Date | null): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(value);
}

function isTrialStatus(value: unknown): boolean {
  const normalized = normalizeStatus(value);
  if (!normalized) return false;
  if (normalized === 'trial' || normalized === 'trialing') return true;
  if (normalized.startsWith('trial') && !normalized.includes('expired') && !normalized.includes('ended') && !normalized.includes('cancel') && !normalized.includes('inactive')) {
    return true;
  }
  return normalized.includes('trial') && !normalized.includes('expired') && !normalized.includes('ended') && !normalized.includes('cancel') && !normalized.includes('inactive');
}

function getTrialEndDate(state?: BillingAccessState | null): Date | null {
  const explicitTrialEnd = parseDate(state?.trialEnd || state?.trial_end || state?.trialEndsAt);
  if (explicitTrialEnd) return explicitTrialEnd;

  if (isTrialStatus(state?.subscriptionStatus || state?.status)) {
    return parseDate(state?.currentPeriodEnd || state?.current_period_end);
  }

  return null;
}

function getTrialStartDate(state?: BillingAccessState | null): Date | null {
  return parseDate(state?.trialStart || state?.trial_start || state?.trialStartsAt);
}

function getCurrentPeriodEndDate(state?: BillingAccessState | null): Date | null {
  return parseDate(state?.currentPeriodEnd || state?.current_period_end);
}

function formatBillingLineLabel(state?: BillingAccessState | null): string {
  const periodEndDate = getCurrentPeriodEndDate(state);
  const formattedPeriodEnd = formatLongDate(periodEndDate);

  if (state?.cancelAtPeriodEnd && formattedPeriodEnd) {
    return `Ends on: ${formattedPeriodEnd}`;
  }

  if (formattedPeriodEnd) {
    return `Next billing: ${formattedPeriodEnd}`;
  }

  return 'Next billing: —';
}

function getTrialDaysRemaining(trialEndDate: Date, now = new Date()): number {
  return Math.max(
    0,
    Math.round((startOfLocalDay(trialEndDate).getTime() - startOfLocalDay(now).getTime()) / 86400000),
  );
}

function getInvoiceAmountLabel(interval?: string | null, pricing?: BillingPricingCopy): string {
  const normalized = String(interval || '').trim().toLowerCase();
  if (normalized.startsWith('year')) return pricing?.yearly?.invoiceAmountLabel || YEARLY_INVOICE_AMOUNT_LABEL;
  return pricing?.monthly?.invoiceAmountLabel || MONTHLY_INVOICE_AMOUNT_LABEL;
}

function formatInvoiceDate(value?: string | null): string {
  const parsed = parseDate(value);
  if (!parsed) return 'Oct 01, 2026';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

function formatInvoiceDateFromDate(value: Date | null): string {
  if (!value) return formatInvoiceDate(null);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(value);
}

function normalizeDownloadFileNamePart(value: string | null | undefined, fallback = 'latest'): string {
  const normalized = String(value || '')
    .trim()
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || fallback;
}

function parseDownloadFileName(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return quotedMatch?.[1] ? quotedMatch[1] : fallback;
}

async function buildInvoiceAuthHeaders() {
  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) throw new Error('missing_access_token');
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  return applyTenantRequestHeaders(headers);
}

async function downloadInvoicePdf(downloadUrl: string, fallbackPrefix = 'Invoice') {
  const response = await fetch(downloadUrl, {
    headers: await buildInvoiceAuthHeaders(),
  });

  if (!response.ok) {
    let message = 'Unable to download invoice.';
    try {
      const body = await response.json();
      message = body?.detail || body?.error || message;
    } catch {
      // keep the default message when the response body is not JSON
    }
    throw new Error(message);
  }

  const parsedUrl = (() => {
    try {
      return new URL(downloadUrl, window.location.origin);
    } catch {
      return null;
    }
  })();
  const invoiceId = parsedUrl?.searchParams.get('invoiceId') || 'latest';

  const blob = await response.blob();
  const fallbackFileName = `${fallbackPrefix}-${normalizeDownloadFileNamePart(invoiceId)}.pdf`;
  const contentDisposition = response.headers.get('content-disposition');
  const parsedFileName = parseDownloadFileName(contentDisposition, fallbackFileName);
  const expectedPrefix = String(fallbackPrefix || '').trim().toLowerCase();
  const fileName = String(parsedFileName || '').trim().toLowerCase().startsWith(expectedPrefix)
    ? parsedFileName
    : fallbackFileName;

  return { blob, fileName };
}

type InvoiceHistoryRow = {
  id: string;
  date: string;
  amount: string;
  status: string;
  canDownload: boolean;
  downloadUrl?: string | null;
  invoicePdfUrl?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
};

function isPaidInvoiceStatus(value: unknown): boolean {
  return String(value || '').trim().toUpperCase() === 'PAID';
}

function shouldDebugInvoiceRows(): boolean {
  try {
    return import.meta.env.DEV || new URLSearchParams(window.location.search).get('debugBillingRows') === '1';
  } catch {
    return !!import.meta.env.DEV;
  }
}

function isProxyInvoiceDownloadUrl(value?: string | null): boolean {
  return String(value || '').trim().startsWith('/api/billing/invoice-pdf');
}

function buildInvoiceDownloadUrl(invoiceId: string): string {
  const params = new URLSearchParams();
  params.set('invoiceId', invoiceId);
  return `/api/billing/invoice-pdf?${params.toString()}`;
}

function buildHistoryRows(state?: BillingAccessState | null, pricing?: BillingPricingCopy, trialDays = 7, isAdminAccount = false): InvoiceHistoryRow[] {
  if (state?.isAdminBillingBypass || state?.isComplimentaryPartnerAccess || isAdminAccount) {
    return [
      {
        id: state?.isComplimentaryPartnerAccess ? 'PARTNER EVALUATION' : 'FULL ACCESS',
        date: '—',
        amount: '$0.00',
        status: state?.isComplimentaryPartnerAccess ? 'EVALUATION' : 'FREE',
        canDownload: false,
        downloadUrl: null,
        invoicePdfUrl: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
      },
    ];
  }

  const normalizedStatus = normalizeStatus(state?.subscriptionStatus || state?.status);
  const isTrialing = normalizedStatus === 'trialing';

  if (isTrialing) {
    const trialEndDate = getTrialEndDate(state);
    const explicitTrialStartDate = getTrialStartDate(state);
    const trialStartDate = explicitTrialStartDate || (trialEndDate
      ? new Date(trialEndDate.getTime() - (Math.max(1, Number(trialDays || 7)) * 86400000))
      : new Date());

    return [
      {
        id: `TRIAL-${new Date().getFullYear()}-001`,
        date: formatInvoiceDateFromDate(trialStartDate),
        amount: '$0.00',
        status: 'TRIAL',
        canDownload: false,
        downloadUrl: null,
        invoicePdfUrl: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
      },
      {
        id: 'Upcoming',
        date: formatInvoiceDate(state?.currentPeriodEnd),
        amount: getInvoiceAmountLabel(state?.plan?.interval, pricing),
        status: 'UPCOMING',
        canDownload: false,
        downloadUrl: null,
        invoicePdfUrl: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
      },
    ];
  }

  const hasPaidSubscription = normalizedStatus === 'active' || normalizedStatus === 'paid' || !!state?.hasAccess;
  const invoiceId = 'INV-2026-001';
  // Accept any real billing anchor: direct PDF URL, subscription ID, or customer ID.
  // The proxy endpoint only needs at least one of the IDs; requiring only stripeSubscriptionId
  // incorrectly hides the icon when a user has stripeCustomerId but no stripeSubscriptionId.
  const hasRealPaidInvoice =
    hasPaidSubscription &&
    (Boolean(state?.invoicePdfUrl) ||
      Boolean(state?.stripeSubscriptionId) ||
      Boolean(state?.stripeCustomerId));
  // Build the download URL: prefer the proxy so CORS and auth are handled server-side.
  // Fall back to the direct Stripe invoice PDF URL only when no IDs are available.
  const proxyAvailable = Boolean(state?.stripeSubscriptionId) || Boolean(state?.stripeCustomerId);
  const resolvedDownloadUrl = hasRealPaidInvoice
    ? proxyAvailable
      ? buildInvoiceDownloadUrl(invoiceId)
      : (state?.invoicePdfUrl || null)
    : null;

  return [{
    id: invoiceId,
    date: formatInvoiceDate(state?.currentPeriodEnd),
    amount: getInvoiceAmountLabel(state?.plan?.interval, pricing),
    status: hasPaidSubscription ? 'PAID' : 'OPEN',
    canDownload: hasRealPaidInvoice,
    downloadUrl: resolvedDownloadUrl,
    invoicePdfUrl: state?.invoicePdfUrl || null,
    stripeCustomerId: state?.stripeCustomerId || null,
    stripeSubscriptionId: state?.stripeSubscriptionId || null,
  }];
}

function buildBillingSummary(state?: BillingAccessState | null, isAdminAccount = false): {
  tone: 'active' | 'trial' | 'demo' | 'past-due' | 'inactive';
  badgeLabel: string;
  secondaryLabel: string;
} {
  if (state?.isAdminBillingBypass || isAdminAccount) {
    return {
      tone: 'active',
      badgeLabel: 'FULL ACCESS',
      secondaryLabel: 'Billing bypass access',
    };
  }
  const now = new Date();
  const normalizedStatus = normalizeStatus(state?.subscriptionStatus || state?.status);
  const trialEndDate = getTrialEndDate(state);
  const periodEndDate = getCurrentPeriodEndDate(state);
  const formattedPeriodEnd = formatLongDate(periodEndDate);
  const trialEndDay = trialEndDate ? startOfLocalDay(trialEndDate).getTime() : null;
  const hasTrialTimeRemaining = trialEndDay != null && trialEndDay >= startOfLocalDay(now).getTime();
  const isCanceledStatus = normalizedStatus === 'canceled'
    || normalizedStatus === 'cancelled'
    || normalizedStatus.includes('canceled')
    || normalizedStatus.includes('cancelled')
    || normalizedStatus === 'ended'
    || normalizedStatus.includes('expired');
  const isTrialLike = isTrialStatus(normalizedStatus);
  const isTrial = !isCanceledStatus && (trialEndDate ? hasTrialTimeRemaining : isTrialLike);
  const isPastDue =
    normalizedStatus === 'past due' ||
    normalizedStatus.includes('past due') ||
    normalizedStatus.includes('pastdue') ||
    normalizedStatus === 'unpaid' ||
    normalizedStatus === 'payment failed' ||
    normalizedStatus === 'incomplete';
  const isActive = !isTrial && !isPastDue && (!!state?.hasAccess || normalizedStatus === 'active');

  if (isTrial) {
    if (!trialEndDate) {
      return {
        tone: 'trial',
        badgeLabel: 'Trial',
        secondaryLabel: 'Trial in progress',
      };
    }

    const daysRemaining = getTrialDaysRemaining(trialEndDate, now);
    if (daysRemaining === 0) {
      return {
        tone: 'trial',
        badgeLabel: 'Trial · Ends today',
        secondaryLabel: 'Trial ends today',
      };
    }

    const dayLabel = `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`;
    return {
      tone: 'trial',
      badgeLabel: `Trial · ${dayLabel}`,
      secondaryLabel: `Trial ends in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`,
    };
  }

  if (isPastDue) {
    return {
      tone: 'past-due',
      badgeLabel: 'Past due',
      secondaryLabel: 'Update your billing details to keep access uninterrupted.',
    };
  }

  if (isActive) {
    if (state?.cancelAtPeriodEnd && formattedPeriodEnd) {
      return {
        tone: 'active',
        badgeLabel: 'Active',
        secondaryLabel: `Subscription ends on ${formattedPeriodEnd}`,
      };
    }

    return {
      tone: 'active',
      badgeLabel: 'Active',
      secondaryLabel: formattedPeriodEnd ? `Next charge: ${formattedPeriodEnd}` : 'Subscription is active.',
    };
  }

  return {
    tone: 'inactive',
    badgeLabel: 'No active subscription',
    secondaryLabel: 'No active subscription',
  };
}

export default function BillingPanel({
  identity,
  initialState = null,
  onStatusChange,
  embedded = false,
  checkoutButtonLabel = 'Start subscription',
  isAdminAccount = false,
  partnerMonthlyPrice,
}: BillingPanelProps) {
  const resolvedIdentity = identity ?? getStoredAppIdentity();
  const branding = getBranding(currentRuntime.brand.id);
  const billingCopy = branding.copy.billing;
  const brandName = currentRuntime.brand.displayName || billingCopy.planName;
  const refundPolicyNote = 'All payments are non-refundable. Cancel anytime before renewal to avoid future charges.';
  const [billingState, setBillingState] = React.useState<BillingAccessState | null>(initialState);
  const [loading, setLoading] = React.useState(!initialState);
  const [actionLoading, setActionLoading] = React.useState<'checkout' | 'portal' | 'invoice' | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const refreshStatus = React.useCallback(async () => {
    if (!resolvedIdentity?.uid) {
      setError('Sign in to manage billing.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const next = await fetchBillingStatus(resolvedIdentity);
      setBillingState(next);
      onStatusChange?.(next);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err ?? 'Unable to load billing status.');
      setError(message || 'Unable to load billing status.');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange, resolvedIdentity]);

  React.useEffect(() => {
    setBillingState(initialState);
    setLoading(!initialState);
    if (initialState) {
      setError(null);
    }
  }, [initialState]);

  React.useEffect(() => {
    if (!initialState) {
      void refreshStatus();
    }
  }, [initialState, refreshStatus]);

  const handleCheckout = async () => {
    setActionLoading('checkout');
    setError(null);
    try {
      const url = await createCheckoutSession(resolvedIdentity);
      window.location.assign(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err ?? 'Unable to start checkout.');
      setError(message || 'Unable to start checkout.');
      setActionLoading(null);
    }
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    setError(null);
    try {
      const url = await createPortalSession(resolvedIdentity);
      window.location.assign(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err ?? 'Unable to open billing management.');
      setError(message || 'Unable to open billing management.');
      setActionLoading(null);
    }
  };

  const handleDownloadInvoice = async (row: InvoiceHistoryRow) => {
    const isPaid = isPaidInvoiceStatus(row.status);
    const hasInvoiceAnchor = !!(row.invoicePdfUrl || row.stripeCustomerId || row.stripeSubscriptionId);
    if (!isPaid || !row.downloadUrl || !hasInvoiceAnchor) {
      setError('Unable to download invoice.');
      return;
    }

    setActionLoading('invoice');
    setError(null);
    try {
      const { blob, fileName } = await downloadInvoicePdf(row.downloadUrl, invoiceFilePrefix);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err ?? 'Unable to download invoice.');
      setError(message || 'Unable to download invoice.');
    } finally {
      setActionLoading(null);
    }
  };

  const state = billingState;
  const summary = buildBillingSummary(state, isAdminAccount);
  const hasActiveSubscription = summary.tone === 'active' || summary.tone === 'trial' || summary.tone === 'demo';
  const planName = String(billingCopy.planName || state?.plan?.name || brandName).trim() || brandName;
  const priceIsYearly = String(state?.plan?.interval || '').trim().toLowerCase().startsWith('year');
  const priceCopy = priceIsYearly ? billingCopy.pricing.yearly : billingCopy.pricing.monthly;
  const configuredMonthlyPrice = typeof partnerMonthlyPrice === 'number' && Number.isFinite(partnerMonthlyPrice)
    ? `$${partnerMonthlyPrice % 1 === 0 ? partnerMonthlyPrice.toFixed(0) : partnerMonthlyPrice.toFixed(2)}`
    : null;
  const priceValue = priceIsYearly ? priceCopy.priceLabel : (configuredMonthlyPrice || priceCopy.priceLabel);
  const priceSuffix = priceCopy.priceSuffix;
  const periodEndDate = getCurrentPeriodEndDate(state);
  const renewalDateLabel = formatLongDate(periodEndDate);
  const isComplimentaryPartnerAccess = state?.isComplimentaryPartnerAccess === true;
  const billingLineLabel = isComplimentaryPartnerAccess ? 'Complimentary partner evaluation' : state?.isAdminBillingBypass ? 'Billing bypass access' : formatBillingLineLabel(state);
  const billingSubtitle = isComplimentaryPartnerAccess
    ? `Complimentary access through ${renewalDateLabel || 'the evaluation expiration date'}.`
    : (state?.isAdminBillingBypass || isAdminAccount)
    ? 'Full access (admin)'
    : hasActiveSubscription
      ? state?.cancelAtPeriodEnd && renewalDateLabel
        ? `Subscription ends on ${renewalDateLabel}.`
        : `Full access (subscription) to ${billingCopy.planName} features.`
      : summary.secondaryLabel;
  const historyRows = buildHistoryRows(state, billingCopy.pricing, billingCopy.trialDays ?? 7, isAdminAccount);
  const invoiceFilePrefix = `${normalizeDownloadFileNamePart(brandName)}-Invoice`;
  const wrapperClass = embedded ? 'w-full' : 'w-full max-w-6xl';
  const rootClass = 'grid grid-cols-1 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300 lg:grid-cols-3';
  const sectionCardClass = 'overflow-hidden rounded-lg border border-border bg-white shadow-sm dark:bg-[#0b0f14] dark:shadow-[0_24px_90px_rgba(0,0,0,0.45)]';
  const panelCardClass = 'rounded-lg border border-border bg-white p-6 shadow-sm dark:bg-[#0b0f14] dark:shadow-[0_24px_90px_rgba(0,0,0,0.45)] md:p-8';
  const statusBadgeClassMap = {
    active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    trial: 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400',
    demo: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20 dark:text-cyan-400',
    'past-due': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    inactive: 'bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20',
  } as const;
  const featureItems = [
    'Smart Trade Import',
    'Deterministic P&L & Metrics',
    'One-Click Broker Connectivity',
    'Advanced Charting & Indicators',
    'Custom Dashboards & Widgets',
    'Risk & Fee Defaults',
  ];

  React.useEffect(() => {
    if (import.meta.env.DEV && hasActiveSubscription && !renewalDateLabel && !billingState?.isAdminBillingBypass) {
      console.warn('[billing] Missing currentPeriodEnd', billingState);
    }
  }, [billingState, hasActiveSubscription, renewalDateLabel]);

  React.useEffect(() => {
    if (!shouldDebugInvoiceRows()) {
      return;
    }
    historyRows.forEach((row) => {
      console.log('ROW DEBUG', row);
    });
  }, [historyRows]);

  const renderLoadingState = () => (
    <div className={rootClass} aria-busy="true">
      <div className="space-y-5 lg:col-span-2">
        <section className={sectionCardClass}>
          <div className="animate-pulse p-8">
            <div className="mb-8">
              <div className="h-3 w-24 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="h-7 w-48 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
                  <div className="mt-3 h-4 w-72 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
                </div>
                <div className="h-14 w-32 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 border-y border-border py-6 md:grid-cols-2">
              <div className="h-6 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
              <div className="h-6 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
              <div className="h-6 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
              <div className="h-6 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
            </div>
            <div className="mt-8 flex gap-4">
              <div className="h-12 w-44 rounded-xl bg-zinc-100 dark:bg-gray-800/80" />
              <div className="h-12 w-32 rounded-xl bg-zinc-100 dark:bg-gray-800/80" />
            </div>
          </div>
        </section>
        <section>
          <div className="mb-4 h-6 w-40 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
          <div className="animate-pulse overflow-hidden rounded-lg border border-border bg-white shadow-sm dark:bg-[#0b0f14] dark:shadow-xl">
            <div className="h-14 bg-zinc-100 dark:bg-gray-800/40" />
            <div className="h-16 border-t border-border bg-zinc-100 dark:bg-gray-800/20" />
          </div>
        </section>
      </div>

      <div className="space-y-6">
        <div className={`${panelCardClass} animate-pulse`}>
          <div className="h-4 w-40 rounded-full bg-zinc-100 dark:bg-gray-800/80" />
          <div className="mt-6 h-40 rounded-2xl bg-zinc-100 dark:bg-gray-800/60" />
          <div className="mt-6 h-12 rounded-xl bg-zinc-100 dark:bg-gray-800/80" />
        </div>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={rootClass}>
      <div className="space-y-5 lg:col-span-2">
        <section className={sectionCardClass}>
          <div className="p-8">
            <div className="mb-8">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-gray-500">Subscription</div>
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-xl">
                  <h2 className="text-2xl font-bold text-zinc-950 dark:text-white">{planName}</h2>
                  <p className="mt-1 text-sm text-zinc-600 dark:text-gray-400">{billingSubtitle}</p>
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex items-baseline gap-2">
                    <div className="whitespace-nowrap text-3xl font-bold leading-none text-zinc-950 dark:text-white">
                      {priceValue}<span className="text-lg font-normal text-zinc-500 dark:text-gray-500">{priceSuffix}</span>
                    </div>
                    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusBadgeClassMap[summary.tone]}`}>
                      {summary.badgeLabel}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px] text-zinc-500 dark:text-gray-500">{billingLineLabel}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-10 gap-y-6 border-y border-border py-6 sm:grid-cols-2 lg:grid-cols-3">
              {featureItems.map((feature) => (
                <div key={feature} className="flex items-start gap-4 text-sm text-zinc-700 dark:text-gray-300">
                  <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-blue-500" />
                  <span className="leading-tight">{feature}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              {state?.isAdminBillingBypass || isComplimentaryPartnerAccess ? (
                <button
                  type="button"
                  onClick={() => void refreshStatus()}
                  disabled={loading || actionLoading !== null}
                  className="rounded-xl border border-zinc-200 bg-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600 transition-all hover:bg-zinc-100 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-transparent dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  Refresh
                </button>
              ) : hasActiveSubscription ? (
                <button
                  type="button"
                  onClick={handlePortal}
                  disabled={loading || actionLoading !== null || !state?.customerPortalEligible}
                  className="group flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/20 transition-all hover:from-blue-500 hover:to-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Manage Billing
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={loading || actionLoading !== null || !state?.checkoutEligible}
                  className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg shadow-blue-900/20 transition-all hover:from-blue-500 hover:to-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {actionLoading === 'checkout' ? 'Redirecting…' : checkoutButtonLabel}
                </button>
              )}

              {state?.isAdminBillingBypass || isComplimentaryPartnerAccess ? null : hasActiveSubscription ? (
                <button
                  type="button"
                  onClick={handlePortal}
                  disabled={loading || actionLoading !== null || !state?.customerPortalEligible}
                  className="rounded-xl border border-zinc-200 bg-transparent px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600 transition-all hover:border-rose-500/30 hover:bg-rose-500/5 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-800/80 dark:text-gray-500 dark:hover:border-rose-500/30 dark:hover:bg-rose-500/5 dark:hover:text-rose-300"
                >
                  Cancel Plan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void refreshStatus()}
                  disabled={loading || actionLoading !== null}
                  className="rounded-xl border border-zinc-200 bg-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600 transition-all hover:bg-zinc-100 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-transparent dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  Refresh
                </button>
              )}
            </div>

            {error ? <p className="mt-4 text-sm text-rose-400">{error}</p> : null}
            <p className="mt-4 text-sm leading-6 text-zinc-500 dark:text-gray-500">{refundPolicyNote}</p>
          </div>
        </section>

        <section className="pt-5">
          <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-500 dark:text-gray-500">
            Billing activity
          </div>
          <h3 className="mb-3 px-1 text-2xl font-bold tracking-tight text-zinc-950 dark:text-white">Invoices</h3>
          <div className={sectionCardClass}>
            <table className="w-full text-left">
              <thead>
                <tr className="bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:bg-gray-800/20 dark:text-gray-500">
                  <th className="px-6 py-4">Invoice</th>
                  <th className="px-6 py-4 text-center">Date</th>
                  <th className="px-6 py-4 text-center">Amount</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">DOWNLOAD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-gray-800">
                {historyRows.map((historyRow) => {
                  const normalizedHistoryStatus = String(historyRow.status || '').trim().toUpperCase();
                  const canDownload =
                    isPaidInvoiceStatus(historyRow.status)
                    && !!historyRow.downloadUrl
                    && !!(historyRow.invoicePdfUrl || historyRow.stripeCustomerId || historyRow.stripeSubscriptionId);
                  const useProxyDownload = isProxyInvoiceDownloadUrl(historyRow.downloadUrl);

                  return (
                  <tr key={historyRow.id} className="group text-sm text-zinc-700 transition-colors hover:bg-zinc-100/80 dark:text-gray-300 dark:hover:bg-gray-800/15">
                    <td className="px-6 py-4 font-medium">{historyRow.id}</td>
                    <td className="px-6 py-4 text-center text-zinc-500 dark:text-gray-500">{historyRow.date}</td>
                    <td className="px-6 py-4 text-center font-bold text-zinc-950 dark:text-white">{historyRow.amount}</td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${normalizedHistoryStatus === 'PAID' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : normalizedHistoryStatus === 'TRIAL' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400' : normalizedHistoryStatus === 'UPCOMING' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : normalizedHistoryStatus === 'FREE' ? 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20 dark:text-cyan-400' : 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}
                      >
                        {normalizedHistoryStatus || historyRow.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {canDownload ? (
                        <a
                          aria-label={`Download PDF for invoice ${historyRow.id}`}
                          title="Download PDF"
                          href={historyRow.downloadUrl || undefined}
                          target={useProxyDownload ? undefined : '_blank'}
                          rel={useProxyDownload ? undefined : 'noopener noreferrer'}
                          onClick={(event) => {
                            if (!useProxyDownload) {
                              return;
                            }
                            event.preventDefault();
                            if (loading || actionLoading !== null) return;
                            void handleDownloadInvoice(historyRow);
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 transition-all hover:bg-zinc-100 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-white/[0.04] dark:hover:text-blue-400"
                        >
                          <Download size={16} />
                        </a>
                      ) : (
                        <span className="text-xs text-zinc-400 dark:text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="space-y-6">
        {state?.isAdminBillingBypass ? (
          <section className={panelCardClass}>
            <h4 className="mb-6 px-1 text-2xl font-bold tracking-tight text-zinc-950 dark:text-white">Billing Bypass</h4>
            <p className="text-sm leading-6 text-zinc-600 dark:text-gray-400">
              Your account has administrative billing bypass enabled. No credit card or billing details are required.
            </p>
          </section>
        ) : (
          <section className={panelCardClass}>
            <h4 className="mb-6 px-1 text-2xl font-bold tracking-tight text-zinc-950 dark:text-white">Card on file</h4>

            <div className="relative mb-5 overflow-hidden rounded-lg border border-border bg-zinc-50 p-4 dark:bg-[#0b0f14]">
              <div className="mb-6 flex items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-7 w-11 items-center justify-center rounded border border-border bg-white/90 text-[14px] font-bold text-zinc-900 dark:bg-white/5 dark:text-white">
                    VISA
                  </div>
                  <div className="text-[14px] font-bold tracking-widest text-zinc-700 dark:text-gray-300">•••• 4242</div>
                </div>
                <span className="inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-blue-500/10 text-blue-400 border-blue-500/20">
                  PRIMARY
                </span>
              </div>

              <div className="flex items-end justify-between">
                <div>
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-widest text-zinc-500 dark:text-gray-500">Exp. Date</div>
                  <div className="text-[14px] font-medium text-zinc-700 dark:text-gray-300">12/28</div>
                </div>

              </div>
            </div>

            <button
              type="button"
              onClick={handlePortal}
              disabled={loading || actionLoading !== null || !state?.customerPortalEligible}
              className="w-full rounded-xl border border-border bg-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600 transition-all hover:border-blue-500 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 dark:bg-[#12123b] dark:text-gray-400 dark:hover:border-blue-500 dark:hover:bg-gradient-to-r dark:hover:from-blue-500 dark:hover:to-indigo-500 dark:hover:text-white"
            >
              Update Card
            </button>
          </section>
        )}
      </div>
    </div>
  );

  return (
    <section className={wrapperClass} aria-live="polite">
      {!embedded ? (
        <header className="mb-7">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Billing &amp; Subscription</h2>
          <p className="mt-2 text-sm text-zinc-600/80 dark:text-zinc-400">Manage your subscription and billing details.</p>
        </header>
      ) : null}

      {loading && !state ? renderLoadingState() : renderContent()}
    </section>
  );
}
